/**
 * Admin configuration context
 * Manages system-wide configuration settings for administrators
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminConfig,
  AdminContextType,
  BrandingConfig,
  AuthDatabaseConfig,
  AdminStorageConfig,
  UserDefaultsConfig,
  AgentControlConfig,
  WelcomeMessageConfig,
  UploadLimitsConfig,
} from '@/types/admin.types';
import { DEFAULT_ADMIN_CONFIG } from '@/types/admin.types';
import { adminConfigAPI } from '@/services/adminConfigService';
import { useAuth } from './AuthContext';

const ADMIN_CONFIG_KEY = 'stellar_admin_config';
const ADMIN_CONFIG_QUERY_KEY = ['admin-config'] as const;

/**
 * Merge a backend AdminConfig with build-time defaults so consumers always
 * see every field populated. Also enforces overrides we can never let the
 * backend override:
 * - VITE_BACKEND_URL (from env / runtime config) for authDatabase.backendUrl
 * - feature flags from DEFAULT_ADMIN_CONFIG (build-time, not user-editable)
 */
function mergeWithDefaults(backendConfig: AdminConfig): AdminConfig {
  const merged: AdminConfig = {
    ...DEFAULT_ADMIN_CONFIG,
    ...backendConfig,
    branding: {
      ...DEFAULT_ADMIN_CONFIG.branding,
      ...backendConfig.branding,
    },
    authDatabase: backendConfig.authDatabase
      ? { ...DEFAULT_ADMIN_CONFIG.authDatabase, ...backendConfig.authDatabase }
      : DEFAULT_ADMIN_CONFIG.authDatabase,
    storage: {
      ...DEFAULT_ADMIN_CONFIG.storage,
      ...backendConfig.storage,
    },
    userDefaults: backendConfig.userDefaults
      ? {
          defaults: {
            ...DEFAULT_ADMIN_CONFIG.userDefaults.defaults,
            ...backendConfig.userDefaults.defaults,
          },
          allowOverride: {
            ...DEFAULT_ADMIN_CONFIG.userDefaults.allowOverride,
            ...backendConfig.userDefaults.allowOverride,
          },
        }
      : DEFAULT_ADMIN_CONFIG.userDefaults,
    agentControl: backendConfig.agentControl
      ? { ...DEFAULT_ADMIN_CONFIG.agentControl, ...backendConfig.agentControl }
      : DEFAULT_ADMIN_CONFIG.agentControl,
    welcomeMessage: backendConfig.welcomeMessage
      ? {
          ...DEFAULT_ADMIN_CONFIG.welcomeMessage,
          ...backendConfig.welcomeMessage,
        }
      : DEFAULT_ADMIN_CONFIG.welcomeMessage,
    uploadLimits: backendConfig.uploadLimits
      ? {
          ...DEFAULT_ADMIN_CONFIG.uploadLimits,
          ...backendConfig.uploadLimits,
        }
      : DEFAULT_ADMIN_CONFIG.uploadLimits,
    retention: backendConfig.retention
      ? {
          ...DEFAULT_ADMIN_CONFIG.retention,
          ...backendConfig.retention,
        }
      : DEFAULT_ADMIN_CONFIG.retention,
    features: backendConfig.features
      ? { ...DEFAULT_ADMIN_CONFIG.features, ...backendConfig.features }
      : DEFAULT_ADMIN_CONFIG.features,
  };

  if (import.meta.env.VITE_BACKEND_URL) {
    merged.authDatabase.backendUrl = import.meta.env.VITE_BACKEND_URL;
  }

  if (merged.features) {
    merged.features.enableEntraAuth =
      DEFAULT_ADMIN_CONFIG.features?.enableEntraAuth ?? false;
    merged.features.enableDatabaseAuth =
      DEFAULT_ADMIN_CONFIG.features?.enableDatabaseAuth ?? true;
  }

  return merged;
}

/**
 * Read the localStorage-cached admin config (with the same merging applied
 * to anything written there). Used as React Query placeholderData so cold
 * loads don't flash default values before the backend fetch resolves.
 * Returns null when the cache is missing/corrupt; React Query then falls
 * back to its own loading state.
 */
function readCachedAdminConfig(): AdminConfig | null {
  try {
    const saved = localStorage.getItem(ADMIN_CONFIG_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as AdminConfig;
    return mergeWithDefaults(parsed);
  } catch (error) {
    console.error('[AdminContext] Failed to parse cached admin config:', error);
    return null;
  }
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function useAdmin(): AdminContextType {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}

interface AdminProviderProps {
  children: ReactNode;
}

export function AdminProvider({ children }: AdminProviderProps) {
  // AdminProvider now sits BELOW AuthProvider — AuthContext fetches its
  // own bootstrap config from /api/public-config, so we no longer need to
  // mount above just to write the config to localStorage for AuthContext
  // to read back. That lets us useAuth() here for the load gate, replacing
  // the auth:ready window event we previously used as a workaround.
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const queryClient = useQueryClient();

  // Single source of truth for admin config. React Query handles caching,
  // dedupe, refetch on focus, and retry — replacing the manual setState +
  // localStorage.setItem + manual fetch dance the old code did. We pre-seed
  // placeholderData from localStorage so cold mounts don't flash defaults
  // before the backend fetch resolves; on every successful fetch we mirror
  // the merged value back to localStorage so the next cold mount has a
  // hot cache available.
  const configQuery = useQuery({
    queryKey: ADMIN_CONFIG_QUERY_KEY,
    queryFn: async () => {
      const backendConfig = await adminConfigAPI.getConfig();
      return mergeWithDefaults(backendConfig);
    },
    enabled: isAuthenticated && !isAuthLoading,
    placeholderData: () => readCachedAdminConfig() ?? undefined,
    // Admin config rarely changes during a session; skip the default
    // refetch-on-window-focus burst. Mutations still invalidate this
    // key explicitly via the mergeMutation onSuccess path.
    staleTime: 5 * 60 * 1000,
  });

  // Mirror authoritative fetched values to localStorage.
  useEffect(() => {
    if (configQuery.isSuccess && configQuery.data) {
      try {
        localStorage.setItem(ADMIN_CONFIG_KEY, JSON.stringify(configQuery.data));
      } catch (error) {
        console.error('[AdminContext] Failed to cache admin config:', error);
      }
    }
  }, [configQuery.isSuccess, configQuery.data]);

  const config: AdminConfig =
    configQuery.data ?? readCachedAdminConfig() ?? DEFAULT_ADMIN_CONFIG;
  const isLoading = configQuery.isFetching;

  // Generic mutation: backend merges the partial, returns the new full
  // config. Optimistic update applies the partial to the React Query cache
  // immediately so input fields don't lag waiting for the round-trip; on
  // error we roll back to the previous snapshot.
  const mergeMutation = useMutation({
    mutationFn: (partial: Partial<AdminConfig>) =>
      adminConfigAPI.mergeConfig(partial),
    onMutate: async (partial) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_CONFIG_QUERY_KEY });
      const previous = queryClient.getQueryData<AdminConfig>(
        ADMIN_CONFIG_QUERY_KEY,
      );
      if (previous) {
        const optimistic = mergeWithDefaults({
          ...previous,
          ...partial,
          // Deep-merge the partial into the previous snapshot for fields
          // that are objects, so e.g. updating a single branding field
          // doesn't blow away the other branding fields.
          branding: { ...previous.branding, ...(partial.branding ?? {}) },
          authDatabase: {
            ...previous.authDatabase,
            ...(partial.authDatabase ?? {}),
          },
          storage: { ...previous.storage, ...(partial.storage ?? {}) },
          userDefaults: partial.userDefaults
            ? {
                defaults: {
                  ...previous.userDefaults.defaults,
                  ...(partial.userDefaults.defaults ?? {}),
                },
                allowOverride: {
                  ...previous.userDefaults.allowOverride,
                  ...(partial.userDefaults.allowOverride ?? {}),
                },
              }
            : previous.userDefaults,
          agentControl: {
            ...previous.agentControl,
            ...(partial.agentControl ?? {}),
          },
          welcomeMessage: {
            ...previous.welcomeMessage,
            ...(partial.welcomeMessage ?? {}),
          },
          uploadLimits: {
            ...previous.uploadLimits,
            ...(partial.uploadLimits ?? {}),
          },
          retention: {
            ...previous.retention,
            ...(partial.retention ?? {}),
          },
          features: { ...previous.features, ...(partial.features ?? {}) },
        });
        queryClient.setQueryData<AdminConfig>(
          ADMIN_CONFIG_QUERY_KEY,
          optimistic,
        );
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      console.error('[AdminContext] Failed to sync config to backend:', error);
      if (context?.previous) {
        queryClient.setQueryData<AdminConfig>(
          ADMIN_CONFIG_QUERY_KEY,
          context.previous,
        );
      }
    },
    onSuccess: (backendConfig) => {
      queryClient.setQueryData<AdminConfig>(
        ADMIN_CONFIG_QUERY_KEY,
        mergeWithDefaults(backendConfig),
      );
    },
  });

  // When the signed-in user identity changes (sign in, sign out, or
  // account switch on the same tab), invalidate the admin-config query
  // so it refetches for the new identity. We key off user.id specifically
  // because the user object reference can change for unrelated reasons
  // (e.g. role re-mapping).
  const lastUserIdRef = useRef<string | null>(user?.id ?? null);
  useEffect(() => {
    const currentId = user?.id ?? null;
    if (currentId !== lastUserIdRef.current) {
      lastUserIdRef.current = currentId;
      queryClient.invalidateQueries({ queryKey: ADMIN_CONFIG_QUERY_KEY });
    }
  }, [user?.id, queryClient]);

  // Each updateXyz fires the same merge mutation, scoped to its own field.
  // The mutation handles: optimistic apply to the cache (so the UI updates
  // synchronously and text inputs don't lag), the backend sync, and
  // rollback on failure.
  //
  // The handlers below are intentionally not useCallback-wrapped per the
  // perf-phase1 plan; the eslint-disable directives suppress the
  // exhaustive-deps warning that the contextValue useMemo would otherwise
  // emit for each. Identity changes still propagate via the dep array.
  /* eslint-disable react-hooks/exhaustive-deps */
  const updateBranding = (branding: Partial<BrandingConfig>) => {
    mergeMutation.mutate({ branding } as Partial<AdminConfig>);
  };

  const updateAuthDatabase = (
    authDatabase: Partial<AuthDatabaseConfig>,
  ) => {
    mergeMutation.mutate({ authDatabase } as Partial<AdminConfig>);
  };

  const updateStorage = (storage: Partial<AdminStorageConfig>) => {
    mergeMutation.mutate({ storage } as Partial<AdminConfig>);
  };

  const updateUserDefaults = (userDefaults: Partial<UserDefaultsConfig>) => {
    mergeMutation.mutate({ userDefaults } as Partial<AdminConfig>);
  };

  const updateAgentControl = (agentControl: Partial<AgentControlConfig>) => {
    mergeMutation.mutate({ agentControl } as Partial<AdminConfig>);
  };

  const updateWelcomeMessage = (
    welcomeMessage: Partial<WelcomeMessageConfig>,
  ) => {
    mergeMutation.mutate({ welcomeMessage } as Partial<AdminConfig>);
  };

  const updateUploadLimits = (
    uploadLimits: Partial<UploadLimitsConfig>,
  ) => {
    mergeMutation.mutate({ uploadLimits } as Partial<AdminConfig>);
  };

  const updateFeatures = (features: Partial<AdminConfig['features']>) => {
    mergeMutation.mutate({ features } as Partial<AdminConfig>);
  };
  /* eslint-enable react-hooks/exhaustive-deps */

  const resetMutation = useMutation({
    mutationFn: () => adminConfigAPI.resetConfig(),
    onSuccess: (resetConfigData) => {
      queryClient.setQueryData<AdminConfig>(
        ADMIN_CONFIG_QUERY_KEY,
        mergeWithDefaults(resetConfigData),
      );
    },
    onError: (error) => {
      console.error('[AdminContext] Failed to reset config on backend:', error);
      // Fall back to a local reset so the UI still recovers if backend fails.
      queryClient.setQueryData<AdminConfig>(
        ADMIN_CONFIG_QUERY_KEY,
        DEFAULT_ADMIN_CONFIG,
      );
    },
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  const resetConfig = async () => {
    if (
      confirm(
        'Are you sure you want to reset all admin configuration to defaults?',
      )
    ) {
      await resetMutation.mutateAsync();
    }
  };

  const exportConfig = (): string => {
    return JSON.stringify(config, null, 2);
  };

  const importConfig = (data: string) => {
    try {
      const imported = JSON.parse(data) as AdminConfig;
      queryClient.setQueryData<AdminConfig>(
        ADMIN_CONFIG_QUERY_KEY,
        mergeWithDefaults(imported),
      );
    } catch (error) {
      throw new Error(`Failed to import config: ${error}`);
    }
  };
  /* eslint-enable react-hooks/exhaustive-deps */

  const contextValue = useMemo<AdminContextType>(
    () => ({
      config,
      updateBranding,
      updateAuthDatabase,
      updateStorage,
      updateUserDefaults,
      updateAgentControl,
      updateWelcomeMessage,
      updateUploadLimits,
      updateFeatures,
      resetConfig,
      exportConfig,
      importConfig,
      isAdmin,
      isLoading,
    }),
    [
      config,
      updateBranding,
      updateAuthDatabase,
      updateStorage,
      updateUserDefaults,
      updateAgentControl,
      updateWelcomeMessage,
      updateUploadLimits,
      updateFeatures,
      resetConfig,
      exportConfig,
      importConfig,
      isAdmin,
      isLoading,
    ],
  );

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
}
